import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import bannersModule, { BannerTypes } from '@gin-betting/banners';
import core, { GQL } from '@gin-betting/core';
import { compact, first } from 'lodash';
import { createSelector } from 'reselect';

const {
    actions: { getBannersData },
    selectors: { makeBannerItemsSelector },
} = bannersModule;

const {
    poller: { usePoller },
    utils: { getBettingOptions },
    entities: {
        actions: { getEventLiveOfTheDay, getEventMatchOfTheDay },
    },
} = core;

interface Output {
    items: Array<
        BannerTypes.BannerLiveOfTheDay | BannerTypes.BannerMatchOfTheDay
    >;
    isLoadTimeUp: boolean;
}

const makeSelector = (zoneId: string) => {
    const selectValidLiveOfTheDay = makeBannerItemsSelector(
        zoneId,
        GQL.BannerType.betting_live_of_the_day
    );

    const selectValidMatchOfTheDay = makeBannerItemsSelector(
        zoneId,
        GQL.BannerType.betting_match_of_the_day
    );

    return createSelector(
        selectValidLiveOfTheDay,
        selectValidMatchOfTheDay,
        (liveOfTheDay, matchOfTheDay) => ({
            liveOfTheDay: first(liveOfTheDay),
            matchOfTheDay: first(matchOfTheDay),
        })
    );
};

const useEventsOfTheDay = (zoneId: string): Output => {
    const dispatch = useDispatch();

    const { liveOfTheDay, matchOfTheDay } = useSelector(makeSelector(zoneId));

    const [isLoadTimeUp, setIsLoadTimeUp] = useState(false);

    const { selectedSportIds } = getBettingOptions();

    const variables = { sportIds: selectedSportIds };

    useEffect(() => {
        dispatch(getBannersData({ zoneId }));
    }, [dispatch, zoneId]);

    usePoller({
        pollerName: GQL.BannerType.betting_live_of_the_day,
        executableAction: getEventLiveOfTheDay,
        variables,
        isPollingTurnedOff: false,
    });

    usePoller({
        pollerName: GQL.BannerType.betting_match_of_the_day,
        executableAction: getEventMatchOfTheDay,
        variables,
        isPollingTurnedOff: false,
    });

    useEffect(() => {
        setTimeout(() => {
            setIsLoadTimeUp(true);
        }, 5000);
    }, []);

    const items = compact([matchOfTheDay, liveOfTheDay]);

    return {
        items,
        isLoadTimeUp,
    };
};

export default useEventsOfTheDay;
